import React, { useEffect, useRef, useState } from 'react';
import { agentAPI } from '../api/agent';
import PaymentModal from './PaymentModal';
import './AgentAssistant.css';

function AgentAssistant({ selectedPoem, onPoemAdded, fullPage = false }) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
    { role: 'assistant', text: '我是小书童，可以帮你复习已学古诗，也可以说“帮我把《将进酒》加入学习库”。教材外古诗添加后需支付 ¥5 解锁学习。' }
  ]);
  const [loading, setLoading] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [paymentModal, setPaymentModal] = useState({ open: false, poemId: null, poemTitle: '' });
  const pollTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  const buildRagContext = () => {
    if (!selectedPoem) return '';
    return [
      `当前古诗：《${selectedPoem.title}》`,
      `作者：${selectedPoem.author || '未知'}`,
      selectedPoem.dynasty ? `朝代：${selectedPoem.dynasty}` : '',
      `正文：${selectedPoem.content || ''}`
    ].filter(Boolean).join('\n');
  };

  const appendMessage = (role, text) => {
    setMessages(prev => [...prev, { role, text }]);
  };

  const finishPoemAdded = (poemId) => {
    if (poemId && onPoemAdded) {
      onPoemAdded(poemId);
    }
  };

  const handleSuccessfulAdd = (response) => {
    if (!response?.poemId || response.status !== 'SUCCESS') return;
    if (response.needPoemPay) {
      setPaymentModal({
        open: true,
        poemId: response.poemId,
        poemTitle: response.poemTitle || ''
      });
      return;
    }
    finishPoemAdded(response.poemId);
  };

  const startTaskPolling = (taskId, initialResponse) => {
    if (!taskId) return;
    setActiveTaskId(taskId);
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);

    pollTimerRef.current = setInterval(async () => {
      try {
        const task = await agentAPI.getTask(taskId);
        setTaskStatus(task);
        if (task.status === 'SUCCESS' || task.status === 'FAILED') {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          if (task.status === 'SUCCESS' && task.resultPoemId) {
            handleSuccessfulAdd({
              poemId: task.resultPoemId,
              status: 'SUCCESS',
              needPoemPay: initialResponse?.needPoemPay,
              poemTitle: initialResponse?.poemTitle
            });
          }
        }
      } catch (error) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }, 2000);
  };

  const sendAgentMessage = async (text, confirmed = false) => {
    const response = await agentAPI.chat({
      message: text,
      confirmed,
      ragContext: buildRagContext(),
      currentPoemId: selectedPoem?.id
    });

    appendMessage('assistant', response.answer || response.message || '已处理');
    if (response.type === 'CONFIRMATION_REQUIRED') {
      setPendingConfirmation({ message: text, prompt: response.message });
      return response;
    }
    setPendingConfirmation(null);

    if (response.taskId) {
      setTaskStatus({ taskId: response.taskId, status: response.status, message: response.message });
      if (response.status === 'SUCCESS') {
        handleSuccessfulAdd(response);
      } else {
        startTaskPolling(response.taskId, response);
      }
    } else if (response.poemId && response.status === 'SUCCESS') {
      handleSuccessfulAdd(response);
    }
    return response;
  };

  const handleSend = async () => {
    const text = message.trim();
    if (!text || loading) return;

    setMessage('');
    appendMessage('user', text);
    setLoading(true);

    try {
      await sendAgentMessage(text, false);
    } catch (error) {
      appendMessage('assistant', error.message || '小助手暂时不可用，请稍后再试。');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAdd = async () => {
    if (!pendingConfirmation || loading) return;
    appendMessage('user', '确认添加');
    setLoading(true);
    try {
      await sendAgentMessage(pendingConfirmation.message, true);
    } catch (error) {
      appendMessage('assistant', error.message || '添加失败，请稍后再试。');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    const poemId = paymentModal.poemId;
    setPaymentModal({ open: false, poemId: null, poemTitle: '' });
    appendMessage('assistant', '支付成功！现在可以开始学习这首古诗了。');
    finishPoemAdded(poemId);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`agent-assistant ${fullPage ? 'full-page' : ''}`}>
      {!fullPage && (
        <div className="agent-assistant-header">
          <span>小书童</span>
          {activeTaskId && <small>任务 #{activeTaskId}</small>}
        </div>
      )}

      <div className="agent-messages">
        {messages.map((item, index) => (
          <div key={index} className={`agent-message ${item.role}`}>
            {item.text}
          </div>
        ))}
        {loading && <div className="agent-message assistant">正在思考...</div>}
      </div>

      {taskStatus && (
        <div className={`agent-task-status ${taskStatus.status?.toLowerCase() || ''}`}>
          <span>{taskStatus.status || 'RUNNING'}</span>
          <p>{taskStatus.message || taskStatus.step || '任务处理中'}</p>
        </div>
      )}

      {pendingConfirmation && (
        <div className="agent-confirm-box">
          <p>{pendingConfirmation.prompt}</p>
          <button onClick={handleConfirmAdd} disabled={loading}>确认添加</button>
          <button onClick={() => setPendingConfirmation(null)} disabled={loading}>取消</button>
        </div>
      )}

      <div className="agent-input-row">
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="问我古诗问题，或让我添加教材外古诗..."
          rows={2}
        />
        <button className="agent-send-btn" onClick={handleSend} disabled={loading || !message.trim()}>
          发送
        </button>
      </div>

      <PaymentModal
        open={paymentModal.open}
        type="POEM_UNLOCK"
        poemId={paymentModal.poemId}
        poemTitle={paymentModal.poemTitle}
        returnPath="/agent-chat"
        onClose={() => setPaymentModal({ open: false, poemId: null, poemTitle: '' })}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
}

export default AgentAssistant;
